// Red banners over the live map: members of this circle with an open SOS
// ("I'm on it", show on map, directions) and the caller's own alert ("I'm safe").
import { sinceText, type MemberRow } from "../live/liveCircle";
import { directionsUrl, ownSos, respondersText, sosBanners } from "../live/sos";
import { sosActionState, type SosActions } from "../live/useSosActions";

export function SosBanners(props: {
  rows: readonly MemberRow[];
  nowMs: number;
  actions: SosActions;
  onSelect: (userId: string) => void;
}) {
  const { rows, nowMs, actions } = props;
  const banners = sosBanners(rows);
  const own = ownSos(rows);
  const ownState = own ? sosActionState(actions, own.alertId) : null;
  // A resolved alert stays until the stream says so; hide it as soon as the server has it.
  const showOwn = own !== null && !ownState?.done;
  if (banners.length === 0 && !showOwn) return null;

  return (
    <div className="sos-banners" aria-live="assertive">
      {showOwn && own && ownState && (
        <section className="sos-banner" aria-label="Your SOS">
          <div className="sos-banner-text">
            <strong>You sent an SOS</strong>
            <span>
              Since {sinceText(nowMs, own.sinceMs)} ·{" "}
              {respondersText(own.responders) ?? "Your circles have been alerted"}
            </span>
            <span className="sos-banner-note">
              This does not contact emergency services. If you are in danger, call your local emergency number.
            </span>
          </div>
          <div className="sos-banner-actions">
            <button type="button" onClick={() => actions.resolve(own.alertId)} disabled={ownState.busy}>
              {ownState.busy ? "Sending…" : "I'm safe"}
            </button>
          </div>
          {ownState.message && <p className="sos-banner-message">{ownState.message}</p>}
        </section>
      )}
      {banners.map((banner) => {
        const state = sosActionState(actions, banner.alertId);
        const acked = banner.ackedBySelf || state.done;
        return (
          <section key={banner.alertId} className="sos-banner" aria-label={`${banner.name}'s SOS`}>
            <div className="sos-banner-text">
              <strong>{banner.name} needs help</strong>
              <span>
                SOS since {sinceText(nowMs, banner.sinceMs)} ·{" "}
                {respondersText(banner.responders, acked) ?? "No one has responded yet"}
              </span>
            </div>
            <div className="sos-banner-actions">
              {!acked && (
                <button type="button" onClick={() => actions.ack(banner.alertId, banner.name)} disabled={state.busy}>
                  {state.busy ? "Sending…" : "I'm on it"}
                </button>
              )}
              {banner.location && (
                <>
                  <button type="button" className="secondary" onClick={() => props.onSelect(banner.userId)}>
                    Show on map
                  </button>
                  <a href={directionsUrl(banner.location)} target="_blank" rel="noopener noreferrer">
                    Directions
                  </a>
                </>
              )}
            </div>
            {state.message && <p className="sos-banner-message">{state.message}</p>}
          </section>
        );
      })}
    </div>
  );
}
